import {
  ref,
  computed,
  inject,
  watch,
} from '@vue/composition-api'
import {
  map,
  each,
  cloneDeep,
  filter,
  find,
  findIndex,
  flatMap,
  isEqual,
  isEmpty,
  includes,
  chain,
  sum,
  sumBy,
  update,
  every,
  first,
} from 'lodash'
import ExtensionProposalApi from '@/api/v2/order/ExtensionProposal'
import useVendingHistory from '@/views/composable/useVendingHistory'
import {
  calcTax as calcTaxOfTable,
  calcTotal as calcTotalOfTable,
  getTotalTax as getTotalTaxOfTemp,
} from '@/views/composable/useExtensionEstimation'

// NOTE: 取得した延長候補からvendingHistoriesTempをつくる（ExtensionCandidateCourses.vueから移設）
export const buildExtensionCandidates = (extensionProposals, buildVendingHistoryTemp) => {
  return extensionProposals.map(extensionProposal => {
    const courseHistoryTemp = buildVendingHistoryTemp({
      vendibleId: extensionProposal.course.vendibleId,
      vendibleType: extensionProposal.course.vendibleType,
      sellingPrice: extensionProposal.course.sellingPrice,
      size: extensionProposal.course.size,
      taxCharge: extensionProposal.course.taxCharge,
    })

    const nominationHistoriesTemp = extensionProposal.nominations.map(nomination => {
      return buildVendingHistoryTemp({
        vendibleId: nomination.vendibleId,
        vendibleType: nomination.vendibleType,
        sellingPrice: nomination.sellingPrice,
        size: nomination.size,
        taxCharge: nomination.taxCharge,
        vendibleRecipientsAttributes: nomination.vendibleRecipientsAttributes,
      })
    })

    const itemHistoriesTemp = extensionProposal.items.map(item => {
      return buildVendingHistoryTemp({
        vendibleId: item.vendibleId,
        vendibleType: item.vendibleType,
        sellingPrice: item.sellingPrice,
        size: item.size,
        taxCharge: item.taxCharge,
        vendibleRecipientsAttributes: item.vendibleRecipientsAttributes,
      })
    })

    return {
      courseHistoryTemp,
      nominationHistoriesTemp,
      itemHistoriesTemp,
      newHistoriesTemp: [],
    }
  })
}

// NOTE: 「前回と同じ延長」をおすすめとして提示する。直近のコース履歴と同じ商品の提案indexを返す
export const pickRecommendedProposalIndex = (extensionProposals, vendingHistories) => {
  const latestCourseHistory = chain(vendingHistories)
    .filter(vendingHistory => vendingHistory.attributes.vendibleType === 'Course')
    .orderBy(['attributes.persistenceReport.data.attributes.startAt'], ['desc'])
    .first()
    .value()

  if (!latestCourseHistory) return -1

  return findIndex(extensionProposals, extensionProposal => {
    return Number(extensionProposal.course.vendibleId) === Number(latestCourseHistory.attributes.vendibleId)
  })
}

export default props => {
  const nominations = inject('nominations', [])
  const users = inject('users', [])

  const extensionCandidates = ref([])
  const isInitializing = ref(true)
  const estimatedTotals = ref([])
  const extensionProposals = ref([])
  const calculationSequence = ref(0)
  const taxSequence = ref(0)
  const fetchSequence = ref(0)
  const isCalculating = ref(false)

  const {
    buildVendingHistoryTemp,
    setVendingContributionsAttributes,
    buildVendingHistoriesToSubmit,
  } = useVendingHistory()

  const vendingHistoriesTempOfArray = computed(() => {
    return map(extensionCandidates.value, extensionCandidate => {
      const vendingHistoriesTemp = [
        extensionCandidate.courseHistoryTemp,
        ...extensionCandidate.nominationHistoriesTemp,
        ...extensionCandidate.itemHistoriesTemp,
        ...extensionCandidate.newHistoriesTemp,
      ]

      return vendingHistoriesTemp
    })
  })

  const vendingHistoriesToSubmitOfArray = computed(() => {
    return map(vendingHistoriesTempOfArray.value, arr => {
      return buildVendingHistoriesToSubmit(arr)
    })
  })

  // NOTE: TAX計算をさせるwatch対象
  const taxEstimationTargets = computed(() => {
    return flatMap(vendingHistoriesTempOfArray.value, arr => {
      return flatMap(arr, o => {
        const { sellingPrice, taxCharge } = o.value

        return [sellingPrice, taxCharge]
      })
    })
  })

  // NOTE: 大計計算をさせるwatch対象
  const totalEstimationTargets = computed(() => {
    // NOTE TAXの計算結果たち（注文内容の変化に応じてtax計算されるので）
    const estimatedTaxPrices = flatMap(vendingHistoriesTempOfArray.value, arr => {
      return flatMap(arr, o => {
        return o.estimatedTaxPrice
      })
    })

    // NOTE 注文する内容の個数
    const len = sum(map(vendingHistoriesToSubmitOfArray.value, arr => arr.length))

    // NOTE sellingPriceとsizeも含める（cutの場合でもsellingPrice変更時に再計算するため）
    const sellingPricesAndSizes = flatMap(vendingHistoriesTempOfArray.value, arr => {
      return flatMap(arr, o => {
        return [o.value.sellingPrice, o.size]
      })
    })

    return [...estimatedTaxPrices, len, ...sellingPricesAndSizes]
  })

  // NOTE: 現在選択中の指名ユーザーからNominationDisplayを表示させる
  const currentNominationsTempOfArray = computed(() => {
    return map(extensionCandidates.value, extensionCandidate => {
      const validNominationHistoriesTemp = filter(
        extensionCandidate.nominationHistoriesTemp,
        nominationHistoryTemp => {
          return every(nominationHistoryTemp.value.vendibleRecipientsAttributes, 'userId')
            && nominationHistoryTemp.value.vendibleId
        },
      )

      return chain(validNominationHistoriesTemp)
        .map(nominationHistoryTemp => {
          // NOTE:
          // vendibleRecipientsは複数想定だが指名のときは一つだけしか基本的に持たない(入力のUIもそうなってる)
          // そのためfirstをつかっている
          const nominee = find(users.value, user => Number(user.id) === Number(first(nominationHistoryTemp.value.vendibleRecipientsAttributes)?.userId))
          const targetNomination = find(nominations.value, nomination => Number(nomination.id) === Number(nominationHistoryTemp.value.vendibleId))

          if (!(nominee && targetNomination)) return null

          const vendingHistoryMock = {
            id: Math.random(),
            meta: {
              color: targetNomination.attributes?.color,
            },
            attributes: {
              vendibleRecipients: {
                data: [{
                  attributes: {
                    recipient: { data: nominee },
                  },
                }],
              },
              vendibleId: targetNomination.id,
            },
          }

          return vendingHistoryMock
        })
        .compact()
        .value()
    })
  })

  const initVendingHistoriesTemp = incomingExtensionProposals => {
    extensionCandidates.value = buildExtensionCandidates(incomingExtensionProposals, buildVendingHistoryTemp)
  }

  const fetchExtensionProposals = async () => {
    fetchSequence.value += 1
    const currentSequence = fetchSequence.value

    try {
      const res = await ExtensionProposalApi.getExtensionProposals({ tableId: props.tableId })

      // NOTE: 並列呼び出し時に古いレスポンスで上書きしないよう、最新のシーケンスのみ反映
      if (fetchSequence.value !== currentSequence) return

      if (res?.data) {
        extensionProposals.value = res.data
        initVendingHistoriesTemp(extensionProposals.value)
      }
    } finally {
      // NOTE: API例外時もスケルトンローダーを解除する
      if (fetchSequence.value === currentSequence) {
        isInitializing.value = false
      }
    }
  }

  const removeNominationHistoryTemp = (extensionCandidateIdx, nominationHistoryTempValue) => {
    const targetNominationHistoriesTemp = extensionCandidates.value[extensionCandidateIdx].nominationHistoriesTemp
    const index = findIndex(targetNominationHistoriesTemp, { value: nominationHistoryTempValue })

    targetNominationHistoriesTemp.splice(index, 1)
  }

  const removeItemHistoryTemp = (extensionCandidateIdx, itemHistoryTempValue) => {
    const targetItemHistoriesTemp = extensionCandidates.value[extensionCandidateIdx].itemHistoriesTemp
    const index = findIndex(targetItemHistoriesTemp, { value: itemHistoryTempValue })

    targetItemHistoriesTemp.splice(index, 1)
  }

  const removeNewHistoryTemp = (extensionCandidateIdx, newHistoryTempValue) => {
    const targetNewHistoriesTemp = extensionCandidates.value[extensionCandidateIdx].newHistoriesTemp
    const index = findIndex(targetNewHistoriesTemp, { value: newHistoryTempValue })

    targetNewHistoriesTemp.splice(index, 1)
  }

  const calcTax = price => calcTaxOfTable({ tableId: props.tableId, price })

  const calcTotal = orderPrice => calcTotalOfTable({ tableId: props.tableId, orderPrice })

  const getSubtotal = vendingHistoriesTemp => {
    return sumBy(vendingHistoriesTemp, vendingHistoryTemp => {
      return vendingHistoryTemp.size * vendingHistoryTemp.value.sellingPrice
    })
  }

  const getTotalTax = vendingHistoriesTemp => getTotalTaxOfTemp(vendingHistoriesTemp)

  const getTotal = index => {
    const estimatedTotalObj = find(estimatedTotals.value, o => o.extensionCandidateIdx === index)

    return estimatedTotalObj?.amount
  }

  const resetCandidates = () => {
    extensionProposals.value = []
    extensionCandidates.value = []
    isInitializing.value = true
    estimatedTotals.value = []
    isCalculating.value = false
  }

  // NOTE: 親の客数変更に依存してsizeも変える
  watch(() => props.customerCount, (newVal, _prev) => {
    each(extensionCandidates.value, extensionCandidate => {
      // eslint-disable-next-line no-param-reassign
      extensionCandidate.courseHistoryTemp.size = newVal
    })
  })

  // NOTE: 販売貢献人をvendingHistoryTempに自動セットするwatch
  watch(() => cloneDeep(vendingHistoriesTempOfArray.value), (_newVal, _prevVal) => {
    each(vendingHistoriesTempOfArray.value, vendingHistoriesTemp => {
      setVendingContributionsAttributes({
        targetVendingHistoriesTemp: vendingHistoriesTemp,
      })
    })
  })

  // NOTE: 全候補の全明細の税サを計算し直す。完了時に自分のシーケンス番号を返すので、
  // 呼び出し側は「自分の計算が最新か」を見てから大計に進める
  const runTaxEstimations = () => {
    taxSequence.value += 1
    const currentSequence = taxSequence.value

    const promises = flatMap(vendingHistoriesTempOfArray.value, vendingHistoriesTemp => {
      return map(vendingHistoriesTemp, vendingHistoryTemp => {
        if (vendingHistoryTemp.value.taxCharge !== 'normal') {
          update(vendingHistoryTemp, 'estimatedTaxPrice', () => 0)

          return Promise.resolve()
        }

        return calcTax(vendingHistoryTemp.value.sellingPrice).then(taxPrice => {
          // NOTE: 古いレスポンスで新しい計算結果を上書きしない
          if (taxSequence.value !== currentSequence) return

          update(vendingHistoryTemp, 'estimatedTaxPrice', () => taxPrice)
        })
      })
    })

    return Promise.all(promises).then(() => currentSequence)
  }

  // NOTE: 全候補の大計（注文後のお会計）を計算し直す
  const runTotalEstimations = () => {
    // 新しい計算シーケンスを開始
    calculationSequence.value += 1
    const currentSequence = calculationSequence.value
    estimatedTotals.value = []
    isCalculating.value = true

    const promises = map(vendingHistoriesTempOfArray.value, (vendingHistoriesTemp, index) => {
      const rawOrderTotal = chain(vendingHistoriesTemp)
        .map(vendingHistoryTemp => {
          const sellingPrice = Number(vendingHistoryTemp.value.sellingPrice)
          const estimatedTaxPrice = Number(vendingHistoryTemp.estimatedTaxPrice)

          return (sellingPrice + estimatedTaxPrice) * vendingHistoryTemp.size
        })
        .sum()
        .value()

      return calcTotal(rawOrderTotal).then(total => {
        // 最新の計算シーケンスでのみ結果を反映
        if (calculationSequence.value !== currentSequence) return

        const existingIndex = findIndex(estimatedTotals.value, o => o.extensionCandidateIdx === index)
        if (existingIndex >= 0) {
          estimatedTotals.value[existingIndex].amount = total
        } else {
          estimatedTotals.value.push({
            extensionCandidateIdx: index,
            amount: total,
          })
        }
      })
    })

    return Promise.all(promises).then(() => {
      // 最新の計算シーケンスでのみ完了フラグを更新
      if (calculationSequence.value === currentSequence) {
        isCalculating.value = false
      }
    })
  }

  // NOTE: 候補の配列ごと差し替えたとき（設定中プランの上書きなど）に使う。
  // 差し替え直後は全明細のestimatedTaxPriceがnullに戻るうえ、
  // 単価・税区分が変わっていなければwatchのガード（isEqual・null混在）で
  // 再計算が一切走らず、概算が古いまま／出ないままになる。
  // watch任せにせず税サ→大計を明示的に回して全候補分を出し直す
  const recalculateEstimations = async () => {
    const currentSequence = await runTaxEstimations()

    // NOTE: 待っている間に新しい税サ計算が始まっていたら、そちらの結果に任せる
    if (currentSequence !== taxSequence.value) return

    await runTotalEstimations()
  }

  // NOTE: 概算を出すために税サを計算
  watch(() => cloneDeep(taxEstimationTargets.value), (newVal, prevVal) => {
    if (isEqual(newVal, prevVal)) return

    runTaxEstimations()
  })

  // NOTE: 概算を出すために大計を計算
  watch(() => cloneDeep(totalEstimationTargets.value), (newVal, prevVal) => {
    if (isEmpty(newVal)) return
    // NOTE: 税サ計算待ちの行があるうちは大計を出さない（途中の金額を見せない）
    if (includes(newVal, null)) return
    if (isEqual(newVal, [0])) return
    if (isEqual(newVal, prevVal)) return

    runTotalEstimations()
  })

  return {
    // data
    extensionProposals,
    extensionCandidates,
    isInitializing,
    estimatedTotals,
    isCalculating,

    // computed
    vendingHistoriesTempOfArray,
    vendingHistoriesToSubmitOfArray,
    taxEstimationTargets,
    totalEstimationTargets,
    currentNominationsTempOfArray,

    // methods
    fetchExtensionProposals,
    initVendingHistoriesTemp,
    removeNominationHistoryTemp,
    removeItemHistoryTemp,
    removeNewHistoryTemp,
    getSubtotal,
    getTotalTax,
    getTotal,
    resetCandidates,
    recalculateEstimations,
  }
}
